import type { ReactNode } from 'react';
import { ApiError, api } from '../../api/client';
import { dropKeptRows } from '../../api/paging';
import type { Session } from '../../api/session';
import {
  ADMIN_ROLES,
  CONTENT_MODERATOR_ROLES,
  CHARACTER_KIND_LABELS,
  DORM_LABELS,
  FORM_MODERATOR_ROLES,
  STAFF_ROLES,
  STUDY_CLASS_LABELS,
  type CharacterKind,
  type Dorm,
  type ModerationTarget,
  type Page,
  type StudyClass,
  type UserRole,
} from '../../api/types';
import { t } from '../../i18n';
import { Action, ActionRow, Dialog, Gutter, Notice, Prose } from '../../shared/ui';

/**
 * Общий инструментарий панелей модерации и админки.
 *
 * Живёт отдельным файлом, а не внутри одной из панелей: тринадцать экранов
 * этого набора делят права, диалог и оформление сообщений, и копия этого
 * кода в каждом файле разъехалась бы на первой же правке.
 *
 * ВАЖНО про диалоги: в App.tsx у SplitLayout нет слотов popout и modal, а
 * менять его нельзя. Поэтому штатные ModalRoot и Alert из VKUI здесь не
 * применимы — диалог рисуется своим оверлеем через портал в document.body.
 * Портал нужен именно из-за position: fixed: внутри панели с трансформацией
 * (а View трансформирует панели на переходах) fixed считается от панели,
 * а не от экрана, и окно уезжает вместе с прокруткой.
 */

/* ── Формат ──────────────────────────────────────────────────────────────── */

export { formatDate, formatDateTime } from '../../shared/lib/date';

export function vkProfileUrl(vkUserId: number): string {
  return `https://vk.com/id${vkUserId}`;
}

/** Имя для показа: у половины записей в вайтлисте его просто нет. */
export function personName(person: { displayName: string | null; vkUserId: number }): string {
  return person.displayName?.trim() || t('review.person.id', { id: person.vkUserId });
}

/* ── Роли ────────────────────────────────────────────────────────────────── */

function hasRole(session: Session, roles: readonly UserRole[]): boolean {
  return session.user !== null && roles.includes(session.user.role);
}

export const isStaffSession = (session: Session): boolean => hasRole(session, STAFF_ROLES);
export const canReviewForms = (session: Session): boolean => hasRole(session, FORM_MODERATOR_ROLES);
export const canReviewContent = (session: Session): boolean =>
  hasRole(session, CONTENT_MODERATOR_ROLES);
export const isAdmin = (session: Session): boolean => hasRole(session, ADMIN_ROLES);

/** Роль ролью, а без права записи любое решение всё равно отобьёт сервер. */
export const canAct = (session: Session): boolean => session.user !== null && session.user.canWrite;

/* ── Наборы значений для выпадающих списков ──────────────────────────────── */

export const DORM_OPTIONS = (Object.keys(DORM_LABELS) as Dorm[]).map((value) => ({
  value,
  label: DORM_LABELS[value],
}));

export const STUDY_CLASS_OPTIONS = (Object.keys(STUDY_CLASS_LABELS) as StudyClass[]).map(
  (value) => ({ value, label: STUDY_CLASS_LABELS[value] }),
);

export const CHARACTER_KIND_OPTIONS = (Object.keys(CHARACTER_KIND_LABELS) as CharacterKind[]).map(
  (value) => ({ value, label: CHARACTER_KIND_LABELS[value] }),
);

/* ── Загрузка данных ─────────────────────────────────────────────────────── */

/**
 * Отдельной карточки у предмета и способности на сервере нет — есть только
 * очередь. Поэтому строку ищем постранично по самой очереди.
 */
export async function findInQueue<T>(
  path: string,
  match: (row: T) => boolean,
  signal: AbortSignal,
): Promise<T | null> {
  const pageSize = 100;
  for (let offset = 0; offset < 2000; offset += pageSize) {
    const page = await api.get<Page<T>>(path, { limit: pageSize, offset }, signal);
    const found = page.items.find(match);
    if (found) return found;
    if (page.items.length < pageSize) return null;
    if (offset + pageSize >= page.total) return null;
  }
  return null;
}

export const QUEUE_KEEP: Record<ModerationTarget, string> = {
  form: 'tasks.form',
  item: 'tasks.item',
  pet: 'tasks.pet',
  ability: 'tasks.ability',
};

/**
 * Решённая заявка уходит из памяти очереди сразу, не дожидаясь перечитывания:
 * вернувшись, проверяющий не увидит её и не откроет второй раз.
 */
export function forgetDecided(target: ModerationTarget, versionId: number): void {
  dropKeptRows<{ versionId: number }>(QUEUE_KEEP[target], (row) => row.versionId === versionId);
}

/* ── Нетронутые поля в разборе ───────────────────────────────────────────── */

/**
 * Сколько нетронутых полей показать сразу.
 *
 * До этого их прятала раскрывашка всегда — и у анкеты с четырнадцатью полями,
 * и у предмета с тремя. В холстах так только у анкеты: у предмета «остальное
 * без изменений» стоит прямо, потому что прятать одну строку за кнопкой —
 * это лишнее нажатие ради экономии одной строки.
 */
export const SAME_FIELDS_FOLD = 5;

/* ── Постраничность ──────────────────────────────────────────────────────── */

/**
 * Кнопка «Показать ещё». onMore = null — дочитывать нечего, и кнопки нет:
 * молча отбиваться кодом 400 на третьей странице (см. PAGE_SIZE) она не
 * должна.
 *
 * Пока идёт чтение, onMore тоже null, поэтому `loading` оставляет кнопку на
 * месте спиннером, если показано ещё не всё: иначе она пропадала бы на
 * каждом нажатии.
 */
export function ShowMore({
  page,
  onMore,
  loading = false,
}: {
  page: Page<unknown> | null;
  onMore: (() => void) | null;
  loading?: boolean;
}) {
  if (!page) return null;
  const waiting = onMore === null && loading && page.items.length < page.total;
  if (!onMore && !waiting) return null;
  return (
    <Gutter>
      {/* Строки «показано N из M» под кнопкой нет: сколько всего, уже написано
          в шапке экрана рядом с названием, а сколько показано — видно по
          самому списку. В макетах её тоже нет ни на одном экране. */}
      <Action tone="more" size="m" stretched loading={waiting} onClick={onMore ?? undefined}>
        {t('common.action.showMore')}
      </Action>
    </Gutter>
  );
}

/* ── Конфликты ───────────────────────────────────────────────────────────── */

/** Текст ровно как в ТЗ: игрок правит заявку, пока она висит на проверке. */
export const STALE_MESSAGE = t('review.stale.title');

export function isConflict(error: unknown): boolean {
  return error instanceof ApiError && error.isConflict;
}

/* ── Оформление сообщений ────────────────────────────────────────────────── */

export type { NoticeTone } from '../../shared/ui';

/**
 * Плашка с сообщением. Своя, а не Snackbar: всплывашка исчезает сама, а
 * «заявку успели изменить» и «отказ без комментария» человек должен увидеть
 * даже если отвлёкся на секунду.
 */
export { Notice };

/** Та же плашка, но отдельным блоком ленты: со швом сверху и полями по бокам. */
export { NoticeBlock } from '../../shared/ui';

export { Tag } from '../../shared/ui';

/* ── Диалоги ─────────────────────────────────────────────────────────────── */

export { Dialog };

/** Диалог «точно?» для необратимых и заметных действий. */
export function ConfirmDialog({
  title,
  text,
  confirmText,
  destructive,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  title: string;
  text: ReactNode;
  confirmText: string;
  destructive?: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog title={title} onClose={busy ? () => undefined : onCancel}>
      <Gutter>
        <Prose>{text}</Prose>
      </Gutter>

      {error ? (
        <Gutter>
          <Notice tone="error" title={t('review.dialog.confirm.error')}>
            {error}
          </Notice>
        </Gutter>
      ) : null}

      <Gutter>
        <ActionRow>
          <Action tone="quiet" size="l" stretched disabled={busy} onClick={onCancel}>
            {t('common.action.cancel')}
          </Action>
          <Action
            tone={destructive ? 'action' : 'accent'}
            size="l"
            stretched
            loading={busy}
            disabled={busy}
            onClick={onConfirm}
          >
            {confirmText}
          </Action>
        </ActionRow>
      </Gutter>
    </Dialog>
  );
}
